import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calculator } from 'lucide-react';

const EMI_FAQS = [
  { q: 'What is a good EMI to salary ratio in India?', a: 'Keep total EMIs under 40% of monthly take-home salary. If your salary is ₹50,000 and EMIs exceed ₹20,000, you may face cash-flow stress.' },
  { q: 'Does EMI change if interest rate changes?', a: 'For floating-rate loans, yes — when the bank raises rates, your EMI may increase or tenure extends. Fixed-rate loans keep the same EMI throughout.' },
  { q: 'Can I reduce my EMI?', a: 'Yes — by making part-prepayments. Each prepayment reduces the outstanding principal, lowering either your EMI amount or remaining tenure.' },
  { q: 'What happens if I miss an EMI?', a: 'A missed EMI attracts a late penalty (1–2% of overdue amount) and hurts your CIBIL score. Two consecutive misses can classify the loan as NPA.' },
  { q: 'How do I calculate EMI for a home loan of 20 lakhs?', a: 'At 8.5% for 20 years: EMI ≈ ₹17,356/month. Total payable ≈ ₹41.65 lakhs. Use the calculator above for your exact numbers.' },
  { q: 'Is EMI the same every month?', a: 'Yes for fixed-rate loans. The split between principal and interest changes — early EMIs are mostly interest; later ones are mostly principal.' },
];

export default function CalcEmi() {
  document.title = 'EMI Calculator India — Budget Mantra';
  const [principal, setPrincipal] = useState('');
  const [rate, setRate]           = useState('');
  const [tenure, setTenure]       = useState('');
  const [emi, setEmi]             = useState(null);

  const calculate = () => {
    const P = parseFloat(principal);
    const r = parseFloat(rate) / 12 / 100;
    const n = parseFloat(tenure);
    if (!P || !r || !n) return;
    setEmi((P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1));
  };

  const fmt = (n) => Math.round(n).toLocaleString('en-IN');

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
          <Calculator size={20} className="text-orange-500" />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold text-stone-900">EMI Calculator</h1>
          <p className="text-sm text-stone-500">Calculate your monthly loan instalment instantly</p>
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-stone-200 p-6 space-y-4 max-w-md">
        <div>
          <label className="block text-xs font-bold text-stone-500 mb-1.5">Loan Amount (₹)</label>
          <input type="number" value={principal} onChange={e => setPrincipal(e.target.value)} placeholder="500000"
            className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-orange-400" />
        </div>
        <div>
          <label className="block text-xs font-bold text-stone-500 mb-1.5">Annual Interest Rate (%)</label>
          <input type="number" value={rate} onChange={e => setRate(e.target.value)} placeholder="8.5"
            className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-orange-400" />
        </div>
        <div>
          <label className="block text-xs font-bold text-stone-500 mb-1.5">Tenure (months)</label>
          <input type="number" value={tenure} onChange={e => setTenure(e.target.value)} placeholder="60"
            className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-orange-400" />
        </div>
        <button onClick={calculate}
          className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-2.5 rounded-xl transition-colors">
          Calculate EMI
        </button>
        {emi !== null && (
          <div className="bg-orange-50 rounded-xl p-4 text-center">
            <p className="text-xs text-stone-500 mb-1">Monthly EMI</p>
            <p className="text-3xl font-extrabold text-orange-600">₹{fmt(emi)}</p>
            <p className="text-xs text-stone-500 mt-2">
              Total payable: ₹{fmt(emi * parseFloat(tenure))} · Interest: ₹{fmt(emi * parseFloat(tenure) - parseFloat(principal))}
            </p>
          </div>
        )}
      </div>
      <p className="mt-6 text-sm text-stone-500">
        Track all your EMIs automatically →{' '}
        <Link to="/login" className="text-orange-500 font-semibold hover:underline">Sign up free</Link>
      </p>
      <div className="mt-10 max-w-2xl space-y-8 text-stone-700 dark:text-stone-300">
        <section>
          <h2 className="text-lg font-extrabold text-stone-900 dark:text-stone-100 mb-2">What is an EMI?</h2>
          <p className="text-sm leading-7">An EMI (Equated Monthly Instalment) is a fixed payment you make to a lender every month until your loan is fully repaid. It covers both the principal and the interest. Whether it's a home loan, car loan, or personal loan — every borrower in India pays an EMI.</p>
        </section>
        <section>
          <h2 className="text-lg font-extrabold text-stone-900 dark:text-stone-100 mb-2">How is EMI calculated?</h2>
          <p className="text-sm leading-7">EMI = [P × r × (1+r)^n] / [(1+r)^n – 1] where P = principal, r = monthly interest rate (annual ÷ 12 ÷ 100), n = tenure in months. Example: ₹5,00,000 at 10% for 60 months → EMI = ₹10,624/month. Total payable = ₹6,37,480.</p>
        </section>
        <section>
          <h2 className="text-lg font-extrabold text-stone-900 dark:text-stone-100 mb-3">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {EMI_FAQS.map(faq => (
              <div key={faq.q} className="border-b border-stone-100 dark:border-stone-800 pb-4">
                <p className="text-sm font-bold text-stone-900 dark:text-stone-100 mb-1">{faq.q}</p>
                <p className="text-sm leading-6 text-stone-600 dark:text-stone-400">{faq.a}</p>
              </div>
            ))}
          </div>
        </section>
        <section>
          <h2 className="text-lg font-extrabold text-stone-900 dark:text-stone-100 mb-2">Related Calculators</h2>
          <div className="flex flex-wrap gap-3">
            <Link to="/calc/sip" className="text-sm font-semibold text-orange-500 hover:underline">SIP Calculator →</Link>
            <Link to="/calc/salary" className="text-sm font-semibold text-orange-500 hover:underline">In-Hand Salary →</Link>
            <Link to="/calc/tax" className="text-sm font-semibold text-orange-500 hover:underline">Income Tax →</Link>
          </div>
        </section>
        <section className="bg-orange-50 dark:bg-orange-950/30 rounded-2xl p-5">
          <p className="text-xs font-bold text-orange-500 uppercase tracking-wide mb-1">Chanakya says</p>
          <p className="text-sm leading-6 text-stone-700 dark:text-stone-300 mb-4">
            {emi !== null
              ? `Your EMI of ₹${fmt(emi)}/month — is that within 40% of your salary? I can check your full EMI burden and tell you if you can comfortably afford this.`
              : `Enter your loan details above and I'll tell you if this EMI fits your financial picture.`}
          </p>
          <Link to="/signup" className="inline-block bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold px-5 py-2 rounded-xl transition-colors">
            Ask Chanakya free →
          </Link>
        </section>
      </div>
    </div>
  );
}
